import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: 'Bearer ' + token() });
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

// §7 categories (keys match the backend fill634 GRID row keys).
// multi: box may hold up to two (hours, code) pairs — only 7F/7H/7I per the form.
// code:  whether a code abbreviation belongs under the number ('opt' = optional, e.g. 7A holiday "H").
// name = the category label; codes = the abbreviations printed with that row on the STD
// 634 (shown in the Category column). codeType decides the in-cell code control:
//   'none'   → 7A: hours only (a capital H can be stamped via double-click, holidays)
//   'fixed'  → one set code for the row (auto-stamped under the number)
//   'select' → 7F/7H/7I: a pull-down menu of that row's codes
const ROWS = [
  { key: 'hrly',        short: '7A', name: 'Hourly / Int PY hrs to be paid', codes: '',   codeType: 'none' },
  { key: 'sick',        short: '7B', name: 'Sick',          codes: 'SL', codeType: 'fixed', fixedCode: 'SL' },
  { key: 'bereavement', short: '7C', name: 'Bereavement',   codes: 'BL', codeType: 'fixed', fixedCode: 'BL' },
  { key: 'vacation',    short: '7D', name: 'Vacation',      codes: 'VA', codeType: 'fixed', fixedCode: 'VA' },
  { key: 'al',          short: '7E', name: 'Annual Leave',  codes: 'AL', codeType: 'fixed', fixedCode: 'AL' },
  { key: 'other',       short: '7F', name: 'Other', codes: 'CT, HC, EX, FM, PH, EL, ML, JD, SW, PL, LY, LX, LV, LP, FH, PV, HI, PT, PA',
    codeType: 'select', codeList: ['CT','HC','EX','FM','PH','EL','ML','JD','SW','PL','LY','LX','LV','LP','FH','PV','HI','PT','PA'] },
  { key: 'dk',          short: '7G', name: 'Dock',          codes: 'DK', codeType: 'fixed', fixedCode: 'DK' },
  { key: 'straight',    short: '7H', name: 'Straight time', codes: 'P, HC, CT, EX', codeType: 'select', codeList: ['P','HC','CT','EX'] },
  { key: 'premium',     short: '7I', name: 'Premium time',  codes: 'CT, P', codeType: 'select', codeList: ['CT','P'] },
];

// §5/§6 checkbox legend — keys MUST match backend fill634 CHECKS keys exactly.
// The employee ticks these by hand (manual; the app does not derive them from §7).
const CHECK_GROUPS = [
  { title: '§5 · Absence with pay — Leave & Sick', items: [
    ['SL_SELF', 'Sick Leave — Self'], ['SL_FAMILY', 'Sick Leave — Family Illness'],
    ['FH', 'Furlough Hours'], ['LY', 'PLP 2025'], ['LX', 'PLP 2020'], ['LV', 'PLP 2012'],
    ['LP', 'PLP 2010'], ['LD', 'Personal Leave 2003'], ['PL', 'Personal Leave'],
  ]},
  { title: '§5 · Absence with pay — Credits & Annual', items: [
    ['AL', 'Annual Leave'], ['VA', 'Vacation'], ['BL', 'Bereavement Leave'],
    ['CT', 'Using Overtime Credits (CTO)'], ['HC', 'Using Holiday Credits'],
    ['EX', 'Using Excess Hours Credit'], ['PH', 'Using Personal Holiday'],
    ['EL', 'Paid Educational Leave'], ['PT', 'Professional Development Day'],
    ['HI', 'Holiday Informal Time Off'],
  ]},
  { title: '§5 · Absence with pay — Military & Disability', items: [
    ['PV', 'Voluntary Personal Leave'], ['PA', 'PARR'], ['C', 'Catastrophic Leave Donations'],
    ['ML', 'Short-Term Military Leave'], ['NDI', 'Nonindustrial Injury'],
    ['TD', 'Temporary Disability'], ['IDL', 'Industrial Disability Leave'],
    ['IDL_S', 'IDL with Supplementation'],
  ]},
  { title: '§5 · Absence with pay — Jury & Witness', items: [
    ['JD', 'Jury Duty'], ['ATT_REMIT', 'Attendance Fee to be Remitted'],
    ['ATT_NONE', 'No Attendance Fees Received'], ['SW', 'Witness'],
    ['CIVIL', 'Civil Case'], ['CRIMINAL', 'Criminal Case'],
    ['FEES_REMIT', 'Fees to be Remitted'], ['FEES_NONE', 'No Fees Received'],
    ['INT_YES', 'In the Interest of the State — Yes'], ['INT_NO', 'In the Interest of the State — No'],
    ['SUBPOENAED', 'Subpoenaed'], ['EXPERT', 'Expert'],
  ]},
  { title: '§6 · Absence without pay', items: [
    ['DK_INFORMAL_11', 'Informal Leave Granted (≤11 working days)'],
    ['DK_INFORMAL_15', 'Informal Leave Granted (≤15 working days, CSUS)'],
    ['AWOL', 'Absence Without Leave (AWOL)'],
    ['PROBATION', 'Absence While Serving a Probationary Period'],
    ['TEMP_LEAVE', 'Temporary Leave (≤30 calendar days)'],
    ['FMLA_MIL_CARE', 'FMLA Military Caregiver Leave'], ['FMLA', 'FMLA'], ['CFRA', 'CFRA'],
    ['PDL', 'PDL'], ['PP_QUALIFYING', 'Pay Period is — Qualifying'],
    ['PP_NONQUALIFYING', 'Pay Period is — Non Qualifying'],
  ]},
];

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

// CA state employee holidays for a given month/year (rule-based, computed offline).
// Returns day-of-month numbers. No weekend-observed substitution (entry stays manual).
function caHolidayDays(monthIndex, year) {
  const Y = Number(year);
  if (!Y) return [];
  const nth = (weekday, n) => {
    const first = new Date(Y, monthIndex, 1).getDay();
    return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
  };
  const lastWeekday = (weekday) => {
    const dim = new Date(Y, monthIndex + 1, 0).getDate();
    const lastDow = new Date(Y, monthIndex, dim).getDay();
    return dim - ((lastDow - weekday + 7) % 7);
  };
  switch (monthIndex) {
    case 0:  return [1, nth(1, 3)];
    case 1:  return [nth(1, 3)];
    case 2:  return [31];
    case 4:  return [lastWeekday(1)];
    case 5:  return [19];
    case 6:  return [4];
    case 8:  return [nth(1, 1)];
    case 10: { const t = nth(4, 4); return [11, t, t + 1]; }
    case 11: return [25];
    default: return [];
  }
}

export default function Timesheet634({ currentUser }) {
  const now = new Date();
  const [pp, setPp] = useState({ month: MONTHS[now.getMonth()], year: String(now.getFullYear()), half: '', workweek: '' });
  // §2 Name: first/last authoritative from the Google/OAuth identity (locked); middle optional.
  const [name, setName] = useState({
    firstName: currentUser?.first_name || '',
    middleName: '',
    lastName: currentUser?.last_name || '',
  });
  // §3/§4 + Time Base/WWG/CB-ID — remembered after first entry, prefilled from /prefs.
  const [hdr, setHdr] = useState({ employeeId: '', positionNumber: '', timeBase: '', wwg: '', cbid: '' });
  const [mostUsed, setMostUsed] = useState([]);
  const [cells, setCells] = useState({}); // "row-day" -> [{ hours, code }, ...]
  const [checks, setChecks] = useState({}); // CHECKS key -> true
  const [showChecks, setShowChecks] = useState(true);
  const [reason, setReason] = useState('');
  const [configured, setConfigured] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [signingUrl, setSigningUrl] = useState(null);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(API + '/timesheet/status', { headers: headers() })
      .then(r => setConfigured(!!r.data.configured))
      .catch(() => setConfigured(false));
    // Prefill remembered fields + most-used codes.
    axios.get(API + '/timesheet/prefs', { headers: headers() })
      .then(r => {
        const d = r.data || {};
        if (d.name) setName(n => ({ ...n, firstName: d.name.firstName || n.firstName, lastName: d.name.lastName || n.lastName }));
        setHdr(h => ({
          employeeId: d.employeeId || '', positionNumber: d.positionNumber || '',
          timeBase: d.timeBase || '', wwg: d.wwg || '', cbid: d.cbid || '',
        }));
        setMostUsed(Array.isArray(d.mostUsedCodes) ? d.mostUsedCodes : []);
      })
      .catch(() => { /* first-ever use: nothing remembered yet */ });
  }, []);

  const holidayDays = useMemo(
    () => new Set(caHolidayDays(MONTHS.indexOf(pp.month), pp.year)),
    [pp.month, pp.year]
  );
  // Order a row's code list by this employee's most-used codes first.
  const orderedCodes = (list) => {
    const inList = new Set(list);
    const first = mostUsed.filter(c => inList.has(c));
    return [...first, ...list.filter(c => !first.includes(c))];
  };

  // --- cell helpers (a cell is an array of {hours, code} pairs) ---
  const getCell = (row, d) => cells[`${row}-${d}`] || [];
  const writeCell = (row, d, arr) => setCells(c => {
    const next = { ...c };
    const key = `${row}-${d}`;
    const any = arr.some(p => (p.hours !== '' && p.hours != null) || p.code);
    if (any) next[key] = arr; else delete next[key];
    return next;
  });
  const updatePair = (row, d, i, field, val) => {
    const arr = getCell(row, d).map(p => ({ ...p }));
    while (arr.length <= i) arr.push({ hours: '', code: '' });
    arr[i][field] = val;
    writeCell(row, d, arr);
  };
  const addPair = (row, d) => {
    const arr = getCell(row, d).map(p => ({ ...p }));
    if (arr.length < 2) { arr.push({ hours: '', code: '' }); setCells(c => ({ ...c, [`${row}-${d}`]: arr })); }
  };
  const removePair = (row, d, i) => {
    const arr = getCell(row, d).map(p => ({ ...p }));
    arr.splice(i, 1);
    writeCell(row, d, arr);
  };
  // Set a pair's hours; on a fixed-code row, auto-stamp that row's code underneath.
  const setHours = (rowObj, d, i, val) => {
    const arr = getCell(rowObj.key, d).map(p => ({ ...p }));
    while (arr.length <= i) arr.push({ hours: '', code: '' });
    arr[i].hours = val;
    if (rowObj.codeType === 'fixed' && !arr[i].code) arr[i].code = rowObj.fixedCode;
    writeCell(rowObj.key, d, arr);
  };
  // Double-click a box → stamp a capital H under the number (holiday quick-entry).
  const stampH = (rowObj, d) => {
    let arr = getCell(rowObj.key, d).map(p => ({ ...p })).filter(p => p.hours || p.code);
    if (arr.length === 0) arr = [{ hours: '', code: 'H' }];
    else arr[0].code = 'H';
    writeCell(rowObj.key, d, arr);
  };

  const rowTotal = (row) =>
    DAYS.reduce((s, d) => s + getCell(row, d).reduce((t, p) => t + (parseFloat(p.hours) || 0), 0), 0);
  const grandTotal = useMemo(
    () => Object.values(cells).reduce((s, arr) => s + arr.reduce((t, p) => t + (parseFloat(p.hours) || 0), 0), 0),
    [cells]
  );
  const checkCount = useMemo(() => Object.values(checks).filter(Boolean).length, [checks]);

  const submit = async () => {
    setError('');
    const cellList = Object.entries(cells)
      .map(([k, arr]) => {
        const [row, day] = k.split('-');
        const pairs = arr
          .filter(p => (p.hours !== '' && p.hours != null) || p.code)
          .map(p => ({ hours: p.hours, code: (p.code || '').toUpperCase() }));
        return { row, day: Number(day), pairs };
      })
      .filter(c => c.pairs.length > 0);
    const checkList = Object.keys(checks).filter(k => checks[k]);
    if (cellList.length === 0 && checkList.length === 0) {
      setError('Enter hours for at least one day, or check an absence code, before signing.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(API + '/timesheet/submit', {
        payPeriod: pp,
        employee: { middleName: name.middleName, ...hdr },
        cells: cellList, checks: checkList, reason,
      }, { headers: headers() });
      setSigningUrl(res.data.signingUrl);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not create the timesheet for signing.');
    } finally {
      setSubmitting(false);
    }
  };

  // Detect the DocuSign iframe returning to our origin (?ts_signed=1).
  const onIframeLoad = (e) => {
    try {
      const href = e.target.contentWindow.location.href; // throws while on docusign.net (cross-origin)
      if (href.includes('ts_signed=1')) { setSigningUrl(null); setDone(true); }
    } catch { /* still on DocuSign — ignore */ }
  };

  const label = { display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const input = { width: '100%', padding: '9px 11px', border: '1.5px solid #E5E7EB', borderRadius: 9, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
  const lockedInput = { ...input, background: '#F3F4F6', color: '#374151', cursor: 'not-allowed' };
  const cardStyle = { background: '#fff', border: '1px solid #E6E9EF', borderRadius: 14, padding: '20px 22px', marginBottom: 18, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };

  // One (hours, code) pair inside a §7 cell: hours on top, code underneath (stacked so
  // the column stays narrow — all 31 days fit with no horizontal scroll).
  const pairBlock = (row, d, i) => {
    const p = getCell(row.key, d)[i] || { hours: '', code: '' };
    const base = { width: '100%', border: 'none', textAlign: 'center', outline: 'none', boxSizing: 'border-box', background: 'transparent' };
    // Enter in the number box jumps to the code pull-down (where there is one).
    const toCode = (e) => { if (e.key === 'Enter') { e.preventDefault(); const c = e.currentTarget.parentNode.querySelector('select'); if (c) c.focus(); } };
    const codeTxt = { fontSize: 9, textAlign: 'center', color: '#8A4B1F', fontWeight: 700, lineHeight: 1.1 };
    return (
      <div key={i} style={{ position: 'relative', borderTop: i > 0 ? '1px dashed #E5E7EB' : 'none', paddingTop: i > 0 ? 2 : 0 }}>
        <input
          value={p.hours || ''}
          onChange={e => setHours(row, d, i, e.target.value.replace(/[^0-9.]/g, '').slice(0, 2))}
          onKeyDown={toCode}
          inputMode="decimal"
          autoFocus
          title="Hours"
          style={{ ...base, fontSize: 11, padding: '2px 0', background: p.hours ? '#FFF7ED' : 'transparent' }}
        />
        {/* 7F/7H/7I: pull-down menu of that row's codes (most-used first) */}
        {row.codeType === 'select' && (
          <select value={p.code || ''} title="Pick a code"
            onChange={e => updatePair(row.key, d, i, 'code', e.target.value)}
            style={{ ...base, WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', textAlign: 'center', textAlignLast: 'center', fontSize: 9, padding: '1px 1px', color: '#8A4B1F', fontWeight: 700, cursor: 'pointer', background: p.code ? '#FDF6F0' : '#EFF2F6', border: '1px solid #DCE0E6', borderRadius: 3 }}>
            <option value="">code ▾</option>
            {orderedCodes(row.codeList).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {/* 7B–7E, 7G: fixed code auto-stamped (shows H instead if double-clicked) */}
        {row.codeType === 'fixed' && (p.hours || p.code) && (
          <div style={codeTxt}>{p.code || row.fixedCode}</div>
        )}
        {/* 7A: hours only; shows H only when double-click-stamped */}
        {row.codeType === 'none' && p.code && (
          <div style={codeTxt}>{p.code}</div>
        )}
        {(i > 0 || p.hours || p.code) && (
          <button onClick={() => removePair(row.key, d, i)} title="Delete this entry"
            style={{ position: 'absolute', top: 0, right: 0, width: 12, height: 12, lineHeight: '10px', fontSize: 9, border: 'none', borderRadius: 6, background: '#E5E7EB', color: '#6B7280', cursor: 'pointer', padding: 0 }}>×</button>
        )}
      </div>
    );
  };

  if (configured === false) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>
      <h2 style={{ color: NAVY }}>Timesheet</h2>
      <p>Electronic signing isn't configured on this server yet. Please check back soon.</p>
    </div>;
  }

  if (done) {
    return <div style={{ padding: 60, textAlign: 'center' }}>
      <div style={{ fontSize: 46, marginBottom: 10 }}>✅</div>
      <h2 style={{ color: NAVY, margin: '0 0 8px' }}>Timesheet signed</h2>
      <p style={{ color: '#6B7280' }}>Your signed STD 634 has been sent to Gabriella Lee (Payroll).</p>
      <button onClick={() => { setDone(false); setCells({}); setChecks({}); setReason(''); }}
        style={{ marginTop: 16, padding: '10px 20px', background: ORANGE, color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer' }}>
        Start another
      </button>
    </div>;
  }

  const dayColW = `${(100 - 22) / 31}%`; // even split for the 31 day columns (17% category + 5% total)

  return (
    <div style={{ padding: '22px 26px', margin: '0 auto' }}>
      <h2 style={{ color: NAVY, margin: '0 0 2px' }}>Timesheet — STD 634</h2>
      <p style={{ color: '#8A8A8E', fontSize: 13, margin: '0 0 18px' }}>
        Absence and Additional Time Worked Report. Fill in your hours and codes, then sign electronically — the signed copy is sent to Payroll.
      </p>

      {/* §1–4 header — mirrors the form */}
      <div style={cardStyle}>
        {/* §1 Pay Period + top-right Time Base / WWG / CB-ID */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 12, marginBottom: 14 }}>
          <div><label style={label}>§1 Month</label>
            <select value={pp.month} onChange={e => setPp({ ...pp, month: e.target.value })} style={input}>
              {MONTHS.map(m => <option key={m}>{m}</option>)}
            </select></div>
          <div><label style={label}>Year</label>
            <input value={pp.year} onChange={e => setPp({ ...pp, year: e.target.value })} style={input} /></div>
          <div><label style={label}>Semimonthly</label>
            <select value={pp.half} onChange={e => setPp({ ...pp, half: e.target.value })} style={input}>
              <option value="">Full month</option>
              <option value="first">First half</option>
              <option value="second">Second half</option>
            </select></div>
          <div><label style={label}>Alt. workweek</label>
            <select value={pp.workweek} onChange={e => setPp({ ...pp, workweek: e.target.value })} style={input}>
              <option value="">— None —</option>
              <option value="4/10/40">4/10/40</option>
              <option value="9/8/80">9/8/80</option>
            </select></div>
          <div><label style={label}>Time Base</label>
            <input value={hdr.timeBase} onChange={e => setHdr({ ...hdr, timeBase: e.target.value })} placeholder="1/1" style={input} /></div>
          <div><label style={label}>WWG</label>
            <input value={hdr.wwg} onChange={e => setHdr({ ...hdr, wwg: e.target.value })} placeholder="2" style={input} /></div>
          <div><label style={label}>CB/ID</label>
            <input value={hdr.cbid} onChange={e => setHdr({ ...hdr, cbid: e.target.value })} placeholder="R01" style={input} /></div>
        </div>
        {/* §2 Name (auto from Google · locked) + §3/§4 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1.4fr 1.2fr 1.2fr', gap: 12 }}>
          <div><label style={label}>§2 First name <span style={{ color: '#9CA3AF', fontWeight: 500, textTransform: 'none' }}>· from Google</span></label>
            <input value={name.firstName} readOnly title="Set from your Google sign-in" style={lockedInput} /></div>
          <div><label style={label}>Middle <span style={{ color: '#9CA3AF', fontWeight: 500, textTransform: 'none' }}>· optional</span></label>
            <input value={name.middleName} onChange={e => setName({ ...name, middleName: e.target.value })} style={input} /></div>
          <div><label style={label}>Last name <span style={{ color: '#9CA3AF', fontWeight: 500, textTransform: 'none' }}>· from Google</span></label>
            <input value={name.lastName} readOnly title="Set from your Google sign-in" style={lockedInput} /></div>
          <div><label style={label}>§3 Employee ID</label>
            <input value={hdr.employeeId} onChange={e => setHdr({ ...hdr, employeeId: e.target.value })} style={input} /></div>
          <div><label style={label}>§4 Position number</label>
            <input value={hdr.positionNumber} onChange={e => setHdr({ ...hdr, positionNumber: e.target.value })} style={input} /></div>
        </div>
        <div style={{ fontSize: 11.5, color: '#9CA3AF', marginTop: 8 }}>
          Employee ID, Position Number and Time Base/WWG/CB-ID are remembered after your first timesheet and auto-filled next time.
        </div>
      </div>

      {/* §5/§6 absence code legend (manual checkboxes) — above §7, as on the form */}
      <div style={cardStyle}>
        <button onClick={() => setShowChecks(s => !s)}
          style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
            §5 / §6 · Absence codes {checkCount > 0 && <span style={{ marginLeft: 8, background: ORANGE, color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 11 }}>{checkCount} checked</span>}
          </span>
          <span style={{ color: '#8A8A8E', fontSize: 13 }}>{showChecks ? '▲ Hide' : '▼ Show'}</span>
        </button>
        <div style={{ fontSize: 12, color: '#8A8A8E', marginTop: 4 }}>
          Check each code that applies — the same way you'd mark the paper form. Then write that code in the matching §7 date box below.
        </div>
        {showChecks && (
          <div style={{ marginTop: 14 }}>
            {CHECK_GROUPS.map(group => (
              <div key={group.title} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 7 }}>{group.title}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '6px 16px' }}>
                  {group.items.map(([code, text]) => (
                    <label key={code} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!checks[code]}
                        onChange={e => setChecks(c => ({ ...c, [code]: e.target.checked }))} />
                      <span><strong style={{ color: NAVY }}>{code.replace(/_/g, ' ')}</strong> — {text}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* §7 daily grid — all 31 days + Total, no horizontal scroll */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>§7 · Dates of absences & extra time worked</div>
          <div style={{ fontSize: 11, color: '#8A8A8E', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#FDE8D5', border: '1px solid ' + ORANGE }} />
            Holiday — enter worked hours + <strong style={{ color: '#8A4B1F' }}>H</strong> in 7A
          </div>
        </div>
        <table style={{ borderCollapse: 'collapse', fontSize: 11, width: '100%', tableLayout: 'fixed', border: '1px solid #EDEDF0' }}>
          <thead>
            <tr>
              <th style={{ width: '17%', textAlign: 'left', padding: '6px 8px', background: '#F7F8FA', borderBottom: '1px solid #E5E7EB', color: NAVY, fontSize: 10.5 }}>Category &amp; codes</th>
              {DAYS.map(d => (
                <th key={d} style={{ width: dayColW, padding: '6px 0', background: holidayDays.has(d) ? '#FDE8D5' : '#F7F8FA', borderBottom: '1px solid #E5E7EB', borderLeft: '1px solid #EEE', color: holidayDays.has(d) ? '#8A4B1F' : '#6B7280', fontWeight: holidayDays.has(d) ? 700 : 600, fontSize: 10 }} title={holidayDays.has(d) ? 'CA state holiday' : ''}>{d}</th>
              ))}
              <th style={{ width: '5%', padding: '6px 2px', background: '#EEF2F7', borderBottom: '1px solid #E5E7EB', color: NAVY, fontSize: 10 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map(row => {
              const allowTwo = row.codeType === 'select';
              return (
                <tr key={row.key}>
                  <td style={{ padding: '4px 8px', borderBottom: '1px solid #F1F1F3', verticalAlign: 'top', overflow: 'hidden' }}>
                    <div style={{ fontSize: 11, lineHeight: 1.2 }}>
                      <span style={{ color: NAVY, fontWeight: 700 }}>{row.short}</span>{' '}
                      <span style={{ color: '#374151', fontWeight: 600 }}>{row.name}</span>
                    </div>
                    {row.codes && (
                      <div style={{ fontSize: 9.5, color: '#6B7280', lineHeight: 1.3, marginTop: 2, wordBreak: 'break-word' }}>{row.codes}</div>
                    )}
                  </td>
                  {DAYS.map(d => {
                    const arr = getCell(row.key, d);
                    const lastFilled = arr.length > 0 && (arr[arr.length - 1].hours || arr[arr.length - 1].code);
                    // Every box offers a small + to reveal the first entry (number on top,
                    // code underneath). A second entry is offered only on 7F/7H/7I.
                    const canAdd = arr.length < 2 && (arr.length === 0 || (lastFilled && allowTwo));
                    return (
                      <td key={d} onDoubleClick={() => stampH(row, d)} title="Double-click to stamp H (holiday)"
                        style={{ padding: 0, borderBottom: '1px solid #F1F1F3', borderLeft: '1px solid #F5F5F7', background: holidayDays.has(d) ? '#FFFBF7' : 'transparent', verticalAlign: 'top' }}>
                        {arr.map((_, i) => pairBlock(row, d, i))}
                        {canAdd && (
                          <button onClick={() => addPair(row.key, d)} title={arr.length === 0 ? 'Add entry' : 'Add a second code'}
                            style={{ display: 'block', width: '100%', border: 'none', borderTop: arr.length > 0 ? '1px dashed #EEE' : 'none', background: 'transparent', color: arr.length === 0 ? '#AEB6C2' : ORANGE, fontSize: 12, fontWeight: 800, cursor: 'pointer', padding: arr.length === 0 ? '5px 0' : '1px 0', lineHeight: 1 }}>+</button>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: '3px 2px', textAlign: 'center', fontWeight: 700, color: NAVY, background: '#FAFBFC', borderBottom: '1px solid #F1F1F3', fontSize: 11 }}>{rowTotal(row.key) || ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: '#8A8A8E' }}>
          <span>Tap <span style={{ color: '#AEB6C2', fontWeight: 800 }}>+</span> in any box and type the <strong>number</strong>. 7B–7E/7G stamp their set code automatically; <strong>7F/7H/7I</strong> show a code pull-down (two entries allowed). <strong>Double-click</strong> a box to stamp <strong>H</strong> (holiday) under the number.</span>
          <span style={{ color: NAVY }}>Grand total: <strong>{grandTotal} hrs</strong></span>
        </div>
      </div>

      {/* Reason + submit */}
      <div style={cardStyle}>
        <label style={label}>§8 · Reason for absence or extra hours worked</label>
        <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} style={{ ...input, resize: 'vertical' }} />
      </div>

      {error && <div style={{ color: '#B91C1C', fontSize: 13, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={submit} disabled={submitting}
          style={{ padding: '12px 26px', background: ORANGE, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.6 : 1 }}>
          {submitting ? 'Preparing…' : 'Review & sign'}
        </button>
      </div>

      {/* Embedded signing modal */}
      {signingUrl && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.6)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: NAVY, color: '#fff' }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>Sign your timesheet</span>
            <button onClick={() => setSigningUrl(null)} style={{ background: 'transparent', border: '1px solid #ffffff55', color: '#fff', borderRadius: 7, padding: '5px 12px', cursor: 'pointer' }}>Close</button>
          </div>
          <iframe title="DocuSign" src={signingUrl} onLoad={onIframeLoad} style={{ flex: 1, border: 'none', width: '100%' }} />
        </div>
      )}
    </div>
  );
}
